//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useEffect, useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { Col, Container, Row, ListGroup, Tab, Card, Button, Modal } from "react-bootstrap";
import { ClientSpecificActions } from "./clientSpecificActions.js";
import { useTable, usePagination, useFilters, useGlobalFilter } from "react-table";
import {
    Table,
    Pagination,
    Form,
} from "react-bootstrap";
import AlertMessage from "../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage.js";
import { ManageClientsActions } from "../clients/manageClients/manageClientsActions.js";
import { getPageContentLabel } from "../common/helpers/cmsLabelHelper";

function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter, id },
}) {
    const count = preFilteredRows.length;

    return (
        <Form style={{ display: "flex" }}>
            <Form.Group>
                <Form.Control
                    id={id}
                    value={filterValue || ""}
                    onChange={(e) => {
                        setFilter(e.target.value || undefined);
                    }}
                    placeholder={`Search ${count} records...`}
                    className="mt-2"
                />
            </Form.Group>
        </Form>
    );
}

const ClientTable = ({ columns, data, cmsData }) => {

    const fetchCommonComponentLabel = (key) =>
        getPageContentLabel(cmsData, "CommonComponent", key);

    const returnPageIndexText = (currentPage, TotalPage) => {
        const pageText = fetchCommonComponentLabel("Common.Page");
        let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
        return (
            <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
        )
    }

    const filterTypes = React.useMemo(
        () => ({
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: { pageIndex: 0 },
        },
        useFilters, // useFilters!
        useGlobalFilter,// useGlobalFilter!
        usePagination
    );

    return (
        <Card>
            <Card.Body>
                <Table striped bordered {...getTableProps()} responsive>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>
                                        {column.render("Header")}
                                        {/* Render the columns filter UI */}
                                        <div>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Row className="mt-3">
                    <Col md="6" className="d-flex flex-lg-row flex-column">
                        {returnPageIndexText(pageIndex + 1, pageOptions.length)}
                        <div className="me-2">
                            <span className="me-2"> {fetchCommonComponentLabel('Common.Show')}</span>
                            <Form.Select
                                className="d-inline-block w-auto"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                                aria-label="Size Select"
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="me-2">
                            <span className="me-2">{fetchCommonComponentLabel('Common.GoToPage')}</span>
                            <Form.Control
                                className="d-inline-block"
                                type="number"
                                min="1"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{ width: "75px" }}
                                aria-label="Page Number"
                            />
                        </div>
                    </Col>
                    <Col md="6" className="mt-1">
                        <Pagination className="float-lg-end float-md-end float-left">
                            <Pagination.First
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Prev
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Next
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            />
                            <Pagination.Last
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const ManageClientSpecificContent = ({ cmsData }) => {
    const [clientSpecificCMSData, setClientSpecificCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} });
    const [clientContentList, setClientContentList] = useState({});
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [propState, setPropState] = useState({});
    const [alert, setAlert] = useState({
        message: "",
        type: "",
        showAlert: false
    })
    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })
    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    const fetchClientSpecificLabel = (key) =>
        getPageContentLabel(clientSpecificCMSData, "ClientSpecificContentComponent", key);

    const fetchCommonComponentLabel = (key) =>
        getPageContentLabel(clientSpecificCMSData, "CommonComponent", key);

    const getClientContentList = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        ClientSpecificActions.GetClientContentList().then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    let resData = res.data && res.data
                    setClientContentList(resData)
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const removeClientContent = () => {
        setShowRemoveModal(false);
        let resourceArr = [];
        let deleteOperation = "delete";
        resourceArr.push({ 'Id': propState?.row?.original?.resourceId });

        let payload = {
            "CompanyId": propState?.row?.original?.companyId,
            "ProgramTemplateId": propState?.row?.original?.programTemplateId,
            "ProgramClassId": propState?.row?.original?.programClassId,
            "RightTrackPersonaId": propState?.row?.original?.rightTrackPersonaId,
            "CountryCode": propState?.row?.original?.countryCode,
            "Type": propState?.row?.original?.type,
            "ResourceId": resourceArr,
            "Operation": deleteOperation
        }
        let refAlert = JSON.parse(JSON.stringify(alert));
        ClientSpecificActions.CreateClientContentSave(payload).then(
            (res) => {
                if (res.status === 200) {
                    const removeContentList = [...clientContentList];
                    removeContentList.splice(propState?.row.index, 1);
                    setClientContentList(removeContentList);
                    refAlert.showAlert = true;
                    refAlert.type = "success";
                    refAlert.message = fetchClientSpecificLabel('ClientSpecificContent.DeleteSuccessMessage');
                }
                else if (res.status === 500 || res.status === 400 || res.status === 401) {
                    refAlert.showAlert = true;
                    refAlert.type = "danger";
                    refAlert.message = fetchClientSpecificLabel('ClientSpecificContent.DeleteErrorMessage');
                } else if (res.status === 204) {
                    refAlert.showAlert = true;
                    refAlert.type = "danger";
                    refAlert.message = res ? res?.data : fetchClientSpecificLabel('ClientSpecificContent.DeleteNoResponseMessage');
                }
                setAlert(refAlert)
            },
            (err) => {
                console.log(err);
            }
        );

    }

    const hideRemoveModal = () => {
        setShowRemoveModal(false);
    }

    const showModal = () => {
        let refAlert = JSON.parse(JSON.stringify(alert));
        refAlert.showAlert = false;
        setAlert(refAlert)
    }

    useEffect(() => {
        getClientContentList()
    }, []);

    useEffect(() => {
        setClientSpecificCMSData(cmsData)
    }, [cmsData]);

    const tableColumns = React.useMemo(
        () => {
            const clientSpecificColumnConfig = [
                ["ClientSpecificContent.Persona", "persona"],
                ["ClientSpecificContent.Country", "countryCode"],
                ["ClientSpecificContent.ClientId", "companyId"],
                ["ClientSpecificContent.ProgramTemplate", "programTemplateName"],
                ["ClientSpecificContent.ProgramClass", "programClassName"],
                ["ClientSpecificContent.Type", "type"],
                ["ClientSpecificContent.ResourceId", "resourceId"],
            ];

            const mappedColumns = clientSpecificColumnConfig.map(([labelKey, accessor]) => ({
                id: labelKey,
                Header: fetchClientSpecificLabel(labelKey),
                accessor,
            }));

            return [
                ...mappedColumns,
                {
                    Header: fetchClientSpecificLabel('ClientSpecificContent.Remove'),
                    id: "delete",
                    Filter: false,
                    accessor: (str) => "delete",

                    Cell: (tableProps) => (
                        <>
                            <Button
                                variant="danger"
                                className="btn-square mx-1"
                                onClick={() => {
                                    setPropState(tableProps)
                                    setShowRemoveModal(true);
                                }}
                                aria-label="Remove"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                            {showRemoveModal &&
                                <Card>
                                    <React.Fragment>
                                        <Modal show={showRemoveModal} onHide={hideRemoveModal}>
                                            <Modal.Header closeButton></Modal.Header>
                                            <Modal.Body className="text-center m-3">
                                                <p className="mb-0">
                                                    {fetchClientSpecificLabel('ClientSpecificContent.deleteMessage')}
                                                </p>
                                            </Modal.Body>
                                            <Modal.Footer>
                                                <Button
                                                    variant="secondary"
                                                    onClick={hideRemoveModal}
                                                >
                                                    {fetchCommonComponentLabel('Common.Cancel')}
                                                </Button>{" "}
                                                <Button
                                                    variant="primary"
                                                    onClick={removeClientContent}
                                                >
                                                    {fetchCommonComponentLabel('Common.OK')}
                                                </Button>
                                            </Modal.Footer>
                                        </Modal>
                                    </React.Fragment>
                                </Card>
                            }

                        </>)
                }
            ];
        },
        [clientContentList, showRemoveModal]
    );

    return (
        <Row>
            <Col lg='12'>
                <Container fluid className="p-0">
                    {errorAlert?.showAlert &&
                        <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                    }
                    {alert?.showAlert &&
                        <AlertMessage alertMessage={alert?.message} alertType={alert?.type} showAlert={showModal} />
                    }
                    {clientContentList && clientContentList.length > 0 &&
                        <ClientTable
                            columns={tableColumns && tableColumns}
                            data={clientContentList && clientContentList}
                            cmsData={clientSpecificCMSData?.pageContent && clientSpecificCMSData}
                        />
                    }
                </Container>
            </Col>
        </Row>
    )
}
export default ManageClientSpecificContent;
